import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Wallet, Search, RefreshCw, CheckCircle, CircleDollarSign, User, X } from 'lucide-react';
import { apiEndpoints } from '../../services/api';
import { handleApiError } from '../../services/api';
import toast from 'react-hot-toast';

function DebtsPage() {
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 4 || currentUser?.role === 'SuperAdmin';

  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleAmount, setSettleAmount] = useState('');
  const [settling, setSettling] = useState(false);

  const fetchDebts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiEndpoints.debts.getAll(statusFilter);
      setDebts(response.data || []);
    } catch (error) {
      console.error('Error fetching debts:', error);
      toast.error(t('debts.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchDebts();
  }, [fetchDebts, refreshKey]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return debts;
    const q = searchTerm.toLowerCase();
    return debts.filter(d =>
      (d.memberName || '').toLowerCase().includes(q) ||
      (d.memberEmail || '').toLowerCase().includes(q) ||
      (d.label || '').toLowerCase().includes(q)
    );
  }, [debts, searchTerm]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, d) => {
      acc.owed += Number(d.amountOwed) || 0;
      acc.paid += Number(d.amountPaid) || 0;
      acc.remaining += Number(d.remaining) || 0;
      return acc;
    }, { owed: 0, paid: 0, remaining: 0 });
  }, [filtered]);

  const openSettle = (debt) => {
    setSettleTarget(debt);
    setSettleAmount(String(debt.remaining || ''));
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    if (!settleTarget) return;
    const amount = parseFloat(settleAmount);
    if (!amount || amount <= 0) {
      toast.error(t('debts.invalidAmount'));
      return;
    }
    if (amount > (settleTarget.remaining || 0)) {
      toast.error(t('debts.amountExceeds'));
      return;
    }
    const currentUserId = localStorage.getItem('userId');
    if (!currentUserId) {
      toast.error(t('debts.sessionExpired'));
      return;
    }
    try {
      setSettling(true);
      await apiEndpoints.debts.settle({
        debtId: settleTarget.id,
        amount,
        processedBy: parseInt(currentUserId),
        description: t('debts.settlementTowards', { label: settleTarget.label })
      });
      toast.success(t('debts.settledSuccess'));
      setSettleTarget(null);
      setSettleAmount('');
      setRefreshKey(k => k + 1);
    } catch (error) {
      handleApiError(error, t('debts.failedToSettle'));
    } finally {
      setSettling(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString();

  return (
    <>
      <div className="bg-neutral-50 p-4 sm:p-6 lg:p-8" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} key={refreshKey}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('debts.title')}</h1>
              <p className="mt-2 text-sm text-gray-600">{t('debts.subtitle')}</p>
            </div>
            <button
              onClick={() => setRefreshKey(k => k + 1)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-all text-gray-700 text-sm font-medium w-fit"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{t('common.refresh')}</span>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('debts.totalOwed')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(totals.owed)} DA</p>
                </div>
                <Wallet className="w-9 h-9 text-amber-500" />
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('debts.totalPaid')}</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">{fmt(totals.paid)} DA</p>
                </div>
                <CheckCircle className="w-9 h-9 text-emerald-500" />
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('debts.totalRemaining')}</p>
                  <p className="text-2xl font-bold text-rose-600 mt-1">{fmt(totals.remaining)} DA</p>
                </div>
                <CircleDollarSign className="w-9 h-9 text-rose-500" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('debts.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              {[['open', t('debts.open')], ['settled', t('debts.settled')], ['', t('debts.all')]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === val
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Debt list */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-gray-500">{t('common.loading')}</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-gray-500">{t('debts.empty')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                      <th className="px-6 py-3">{t('debts.member')}</th>
                      <th className="px-6 py-3">{t('debts.label')}</th>
                      <th className="px-6 py-3 text-right">{t('debts.amountOwed')}</th>
                      <th className="px-6 py-3 text-right">{t('debts.amountPaid')}</th>
                      <th className="px-6 py-3 text-right">{t('debts.remaining')}</th>
                      <th className="px-6 py-3">{t('debts.status')}</th>
                      <th className="px-6 py-3 text-right">{t('debts.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(d => (
                      <tr key={d.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-700 font-bold text-sm shrink-0">
                              <User className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{d.memberName || 'Unknown'}</p>
                              {d.memberEmail && <p className="text-xs text-gray-400">{d.memberEmail}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-gray-600">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                            d.memberRole === 'coach'
                              ? 'bg-violet-50 text-violet-600 border-violet-100'
                              : d.memberRole === 'owner'
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                              : 'bg-amber-50 text-amber-600 border-amber-100'
                          }`}>
                            {d.memberRole === 'coach' ? t('debts.coach') : d.memberRole === 'owner' ? t('debts.owner') : t('debts.rider')}
                          </span>
                          <p className="mt-1 text-xs text-gray-500">{d.label}</p>
                        </td>
                        <td className="px-6 py-3 text-right font-bold text-gray-800">{fmt(d.amountOwed)} DA</td>
                        <td className="px-6 py-3 text-right text-emerald-600">{fmt(d.amountPaid)} DA</td>
                        <td className="px-6 py-3 text-right font-bold text-rose-600">{fmt(d.remaining)} DA</td>
                        <td className="px-6 py-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                            d.isSettled
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                              : 'bg-rose-50 text-rose-600 border-rose-100'
                          }`}>
                            {d.isSettled ? t('debts.paid') : t('debts.pending')}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          {!d.isSettled && (
                            <button
                              onClick={() => openSettle(d)}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700"
                            >
                              {t('debts.settle')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settle modal */}
      {settleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{t('debts.settleTitle')}</h3>
              <button onClick={() => setSettleTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSettle} className="p-6 space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 font-medium">{settleTarget.memberName}</p>
                <p className="text-xs text-gray-500 mt-0.5">{settleTarget.label}</p>
                <div className="mt-2 flex justify-between text-sm">
                  <span className="text-gray-500">{t('debts.remaining')}</span>
                  <span className="font-bold text-rose-600">{fmt(settleTarget.remaining)} DA</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('debts.paymentAmount')}</label>
                <input
                  type="number"
                  value={settleAmount}
                  onChange={(e) => setSettleAmount(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min="0"
                  required
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={settling}
                  className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {settling ? t('common.loading') : t('debts.confirmSettle')}
                </button>
                <button
                  type="button"
                  onClick={() => setSettleTarget(null)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200"
                >
                  {t('debts.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default DebtsPage;